"use client";

import Image from "next/image";

import { useState, useRef, useEffect, useCallback, type KeyboardEvent } from "react";
import { cn } from "@/lib/utils";
import { Check, MoveHorizontal } from "lucide-react";

interface WheelOption {
  id: string;
  name: string;
  image: string;
  thumbnail: string;
}

interface InteractiveShowcaseProps {
  beforeImage: string;
  wheels: WheelOption[];
  scenarioName?: string;
  imageClassName?: string;
  beforeImageClassName?: string;
  afterImageClassName?: string;
  priority?: boolean;
}

export function InteractiveShowcase({
  beforeImage,
  wheels,
  scenarioName,
  imageClassName,
  beforeImageClassName,
  afterImageClassName,
  priority = false,
}: InteractiveShowcaseProps) {
  const [sliderPosition, setSliderPosition] = useState(50);
  const [activeWheelId, setActiveWheelId] = useState(wheels[0]?.id);
  const [isResizing, setIsResizing] = useState(false);
  const containerRef = useRef<HTMLDivElement>(null);

  const activeWheel = wheels.find((w) => w.id === activeWheelId) || wheels[0];

  const handleMove = useCallback(
    (clientX: number) => {
      if (!containerRef.current) return;
      const rect = containerRef.current.getBoundingClientRect();
      const x = Math.max(0, Math.min(clientX - rect.left, rect.width));
      const percentage = (x / rect.width) * 100;
      setSliderPosition(percentage);
    },
    []
  );

  const onMouseMove = useCallback(
    (e: MouseEvent) => {
      if (!isResizing) return;
      handleMove(e.clientX);
    },
    [isResizing, handleMove]
  );

  const onTouchMove = useCallback(
    (e: TouchEvent) => {
      if (!isResizing) return;
      handleMove(e.touches[0].clientX);
    },
    [isResizing, handleMove]
  );

  const stopResizing = useCallback(() => {
    setIsResizing(false);
  }, []);

  useEffect(() => {
    if (isResizing) {
      window.addEventListener("mousemove", onMouseMove);
      window.addEventListener("touchmove", onTouchMove);
      window.addEventListener("mouseup", stopResizing);
      window.addEventListener("touchend", stopResizing);
    }
    return () => {
      window.removeEventListener("mousemove", onMouseMove);
      window.removeEventListener("touchmove", onTouchMove);
      window.removeEventListener("mouseup", stopResizing);
      window.removeEventListener("touchend", stopResizing);
    };
  }, [isResizing, onMouseMove, onTouchMove, stopResizing]);

  const handleInteractionStart = (clientX: number) => {
    setIsResizing(true);
    handleMove(clientX);
  };

  const handleKeyDown = useCallback((event: KeyboardEvent<HTMLDivElement>) => {
    const step = event.shiftKey ? 10 : 5;

    if (event.key === "ArrowLeft" || event.key === "ArrowDown") {
      event.preventDefault();
      setSliderPosition((position) => Math.max(0, position - step));
    } else if (event.key === "ArrowRight" || event.key === "ArrowUp") {
      event.preventDefault();
      setSliderPosition((position) => Math.min(100, position + step));
    } else if (event.key === "Home") {
      event.preventDefault();
      setSliderPosition(0);
    } else if (event.key === "End") {
      event.preventDefault();
      setSliderPosition(100);
    }
  }, []);

  return (
    <div className="w-full max-w-5xl mx-auto">
      {/* Main Comparison Area */}
      <div className="relative rounded-2xl overflow-hidden border border-white/10 shadow-2xl bg-[#0A0C0F] group select-none">
        
        {/* Dynamic Header Overlay */}
        <div className="absolute top-0 left-0 right-0 z-20 p-6 flex justify-between items-start pointer-events-none">
          <div className="bg-black/60 backdrop-blur-md px-4 py-2 rounded-full border border-white/10">
            <span className="text-sm font-medium text-white/90">Original</span>
          </div>
          <div className="bg-white/90 backdrop-blur-md px-4 py-2 rounded-full border border-white/30 shadow-[0_0_20px_-5px_rgba(255,255,255,0.2)]">
            <span className="text-sm font-medium text-black">
              <span aria-live="polite">{activeWheel.name}</span>
            </span>
          </div>
        </div>

        <div
          ref={containerRef}
          className="relative aspect-[4/3] md:aspect-[16/9] w-full cursor-col-resize touch-pan-y"
          role="slider"
          tabIndex={0}
          aria-label={`Compare the original ${scenarioName ?? "car"} with ${activeWheel.name}`}
          aria-valuemin={0}
          aria-valuemax={100}
          aria-valuenow={Math.round(sliderPosition)}
          aria-valuetext={`${Math.round(sliderPosition)}% original image`}
          onMouseDown={(e) => handleInteractionStart(e.clientX)}
          onTouchStart={(e) => handleInteractionStart(e.touches[0].clientX)}
          onKeyDown={handleKeyDown}
        >
          {/* Before Image (Background) */}
          <div className={cn("absolute inset-0", beforeImageClassName)}>
            <Image
              src={beforeImage}
              alt="Car photo before wheel visualization"
              fill
              className={cn("object-cover", imageClassName)}
              draggable={false}
              priority={priority}
              sizes="(max-width: 768px) 100vw, 1280px"
            />
          </div>

          {/* After Image (Foreground - Clipped) */}
          <div
            className="absolute inset-0 overflow-hidden"
            style={{ clipPath: `inset(0 0 0 ${sliderPosition}%)` }}
          >
            <div className={cn("relative w-full h-full", afterImageClassName)}>
               <Image
                src={activeWheel.image}
                alt={activeWheel.name}
                fill
                className={cn("object-cover", imageClassName)} 
                draggable={false}
                priority={priority}
                sizes="(max-width: 768px) 100vw, 1280px"
              />
            </div>
          </div>

          {/* Slider Handle */}
          <div
            className="absolute top-0 bottom-0 w-1 bg-white/60 backdrop-blur-sm z-10 cursor-col-resize group-active:bg-white transition-colors"
            style={{ left: `${sliderPosition}%` }}
          >
            <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-10 h-10 bg-white rounded-full shadow-[0_0_20px_rgba(0,0,0,0.5)] flex items-center justify-center transform hover:scale-110 transition-transform">
              <MoveHorizontal className="w-5 h-5 text-black/80" />
            </div>
          </div>
        </div>
      </div>

      {/* Wheel Selector */}
      <div className="mt-8 grid grid-cols-3 gap-4 md:gap-8">
        {wheels.map((wheel) => (
          <button
            key={wheel.id}
            type="button"
            onClick={() => setActiveWheelId(wheel.id)}
            aria-pressed={activeWheelId === wheel.id}
            aria-label={`Show ${wheel.name} on the ${scenarioName ?? "car"}`}
            className={cn(
              "group relative flex flex-col gap-3 p-4 rounded-xl border text-left transition-all duration-300",
              activeWheelId === wheel.id
                ? "bg-white/[0.08] border-white/30 shadow-[0_0_30px_-10px_rgba(255,255,255,0.1)]"
                : "bg-white/[0.02] border-white/5 hover:bg-white/[0.04] hover:border-white/10"
            )}
          >
            <div className="relative aspect-video w-full rounded-lg overflow-hidden bg-black/20">
              <Image 
                src={wheel.thumbnail || wheel.image} 
                alt={wheel.name}
                fill
                className="object-cover transform group-hover:scale-105 transition-transform duration-500"
                sizes="(max-width: 768px) 33vw, 20vw"
              />
              {activeWheelId === wheel.id && (
                <div className="absolute inset-0 bg-white/5 flex items-center justify-center backdrop-blur-[1px] z-10">
                  <div className="w-8 h-8 rounded-full bg-white/90 text-black flex items-center justify-center shadow-lg transform scale-100 animate-in fade-in zoom-in duration-200">
                    <Check className="w-4 h-4" strokeWidth={3} />
                  </div>
                </div>
              )}
            </div>
            
            <div>
              <div className={cn(
                "font-medium transition-colors",
                activeWheelId === wheel.id ? "text-white" : "text-white/70 group-hover:text-white"
              )}>
                {wheel.name}
              </div>
              <div className="text-xs text-white/40 mt-1">
                View Result
              </div>
            </div>
          </button>
        ))}
      </div>
    </div>
  );
}
